import React from 'react';
import PropTypes from 'prop-types';

import AddedSugarsRow from 'components/nutritional_panels/editable_nutrition_facts/AddedSugarsRow';
import EditMainNutritionRow from 'components/nutritional_panels/editable_nutrition_facts/EditMainNutritionRow';
import EditSubNutritionRow from 'components/nutritional_panels/editable_nutrition_facts/EditSubNutritionRow';
import { NUTRITION_ATTRIBUTES } from 'components/upb_viewer/pdp_components/ProductNutritionFactsView';
import useFindAttributeValueByName from 'lib/hooks/product/useFindAttributeValueByName';

function CarbohydratesSection({ nutritionAttributes, setNutritionAttributes }) {
  const carbohydratesQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CARBOHYDRATES_QUANTITYCONTAINED,
  );
  const carbohydratesUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CARBOHYDRATES_UOM,
  );
  const carbohydratesDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CARBOHYDRATES_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const carbohydratesPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.CARBOHYDRATES_MEASUREMENTPRECISIONCODE,
  );

  const totalDietFiberQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_QUANTITYCONTAINED,
  );
  const totalDietFiberUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_UOM,
  );
  const totalDietFiberDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const totalDietFiberPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_MEASUREMENTPRECISIONCODE,
  );

  const solubleFiberQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_QUANTITYCONTAINED,
  );
  const solubleFiberUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_UOM,
  );
  const solubleFiberPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_MEASUREMENTPRECISIONCODE,
  );

  const insolubleFiberQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_QUANTITYCONTAINED,
  );
  const insolubleFiberUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_UOM,
  );
  const insolubleFiberPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_MEASUREMENTPRECISIONCODE,
  );

  const totalSugarQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_SUGAR_QUANTITYCONTAINED,
  );
  const totalSugarUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_SUGAR_UOM,
  );
  const totalSugarPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.TOTAL_SUGAR_MEASUREMENTPRECISIONCODE,
  );

  const addedSugarQuantity = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ADDED_SUGAR_QUANTITYCONTAINED,
  );
  const addedSugarUom = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ADDED_SUGAR_UOM,
  );
  const addedSugarDailyValue = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ADDED_SUGAR_RDI_DAILYVALUEINTAKEPERCENT,
  );
  const addedSugarPrecisionCode = useFindAttributeValueByName(
    nutritionAttributes,
    NUTRITION_ATTRIBUTES.ADDED_SUGAR_MEASUREMENTPRECISIONCODE,
  );

  if (!carbohydratesQuantity) {
    return null;
  }

  return (
    <>
      <EditMainNutritionRow
        title="Total Carbohydrates"
        quantity={carbohydratesQuantity}
        uom={carbohydratesUom}
        percentage={carbohydratesDailyValue}
        precisionCode={carbohydratesPrecisionCode}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.CARBOHYDRATES_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.CARBOHYDRATES_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.CARBOHYDRATES_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.CARBOHYDRATES_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Dietary Fiber"
        quantity={totalDietFiberQuantity}
        uom={totalDietFiberUom}
        percentage={totalDietFiberDailyValue}
        precisionCode={totalDietFiberPrecisionCode}
        offset={1}
        boldPercentage
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.TOTAL_DIETARY_FIBER_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Soluble Fiber"
        quantity={solubleFiberQuantity}
        uom={solubleFiberUom}
        precisionCode={solubleFiberPrecisionCode}
        offset={2}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_UOM}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.SOLUBLE_FIBER_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Insoluble Fiber"
        quantity={insolubleFiberQuantity}
        uom={insolubleFiberUom}
        precisionCode={insolubleFiberPrecisionCode}
        offset={2}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_UOM}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.INSOLUBLE_FIBER_MEASUREMENTPRECISIONCODE}
      />
      <EditSubNutritionRow
        title="Total Sugars"
        quantity={totalSugarQuantity}
        uom={totalSugarUom}
        precisionCode={totalSugarPrecisionCode}
        offset={1}
        separatorOffset={addedSugarQuantity ? 2 : 0}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.TOTAL_SUGAR_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.TOTAL_SUGAR_UOM}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.TOTAL_SUGAR_MEASUREMENTPRECISIONCODE}
      />
      <AddedSugarsRow
        quantity={addedSugarQuantity}
        uom={addedSugarUom}
        percentage={addedSugarDailyValue}
        precisionCode={addedSugarPrecisionCode}
        separatorOffset={addedSugarQuantity ? 2 : 0}
        setNutritionAttributes={setNutritionAttributes}
        quantityAttribute={NUTRITION_ATTRIBUTES.ADDED_SUGAR_QUANTITYCONTAINED}
        uomAttribute={NUTRITION_ATTRIBUTES.ADDED_SUGAR_UOM}
        percentageAttribute={NUTRITION_ATTRIBUTES.ADDED_SUGAR_RDI_DAILYVALUEINTAKEPERCENT}
        precisionCodeAttribute={NUTRITION_ATTRIBUTES.ADDED_SUGAR_MEASUREMENTPRECISIONCODE}
      />
    </>
  );
}

CarbohydratesSection.propTypes = {
  nutritionAttributes: PropTypes.array,
  setNutritionAttributes: PropTypes.func,
};

export default CarbohydratesSection;
